import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../firebase/firebase-config';
import SEO from '../components/SEO';
import { getWorkspace, listContracts, uploadContract, listWorkspaceOpenTasks, setTaskStatus } from '../utils/deskApi';
import ppLogo from '../assets/images/pp-logo.png';
import '../Styles/Desk.css';

const MAX_FILE_BYTES = 8 * 1024 * 1024;
const WATCH_HORIZON_DAYS = 90;

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function factText(f) {
  return f?.text || '';
}

function fmtDueDate(dateStr) {
  const d = new Date(`${dateStr}T00:00:00`);
  return Number.isNaN(d.getTime()) ? dateStr : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

// The dashboard's four ledgers — computed client-side from every persisted
// contract's stored analysis. Small-scale by design (plan caps top out at
// 300 contracts); a server-side aggregation endpoint isn't worth it yet.
function buildLedgers(contracts) {
  const weOwe = [];
  const theyOwe = [];
  const money = [];
  const watch = [];
  const today = new Date().toISOString().slice(0, 10);
  const horizon = new Date(Date.now() + WATCH_HORIZON_DAYS * 86400000).toISOString().slice(0, 10);

  contracts.forEach(c => {
    const a = c.analysis || {};
    (a.deliverables || []).forEach(f => weOwe.push({ contract: c, text: factText(f), page: f.page }));
    (a.client_obligations || []).forEach(f => theyOwe.push({ contract: c, text: factText(f), page: f.page }));
    (a.payment_schedule || []).forEach(p => money.push({
      contract: c,
      text: [p.description, p.amount, p.due].filter(Boolean).join(' — '),
      page: p.page,
    }));
    (a.attention_flags || []).forEach(f => watch.push({ contract: c, text: factText(f), page: f.page, kind: 'flag' }));
    (a.key_dates || []).forEach(d => {
      if (d.date_iso && d.date_iso >= today && d.date_iso <= horizon) {
        watch.push({ contract: c, text: `${d.label}${d.detail ? ` — ${d.detail}` : ''} (${d.date_iso})`, page: d.page, kind: 'date' });
      }
    });
  });

  return { weOwe, theyOwe, money, watch };
}

const MAX_ITEMS_PER_GROUP = 4;

function groupByContract(items) {
  const groups = [];
  const byId = new Map();
  items.forEach(it => {
    let group = byId.get(it.contract.id);
    if (!group) {
      group = { contract: it.contract, items: [] };
      byId.set(it.contract.id, group);
      groups.push(group);
    }
    group.items.push(it);
  });
  return groups;
}

function LedgerCard({ title, hint, items, emptyText }) {
  const groups = groupByContract(items);
  return (
    <div className="desk-ledger">
      <div className="desk-ledger-head">
        <h3>{title}</h3>
        <span>{items.length}</span>
      </div>
      <p className="desk-ledger-hint">{hint}</p>
      {items.length === 0 ? (
        <p className="desk-ledger-empty">{emptyText}</p>
      ) : (
        <div className="desk-ledger-groups">
          {groups.map(g => (
            <div className="desk-ledger-group" key={g.contract.id}>
              <Link to={`/desk/contracts/${g.contract.id}`} className="desk-ledger-source" title={g.contract.filename}>
                {g.contract.filename}
              </Link>
              <ul>
                {g.items.slice(0, MAX_ITEMS_PER_GROUP).map((it, i) => (
                  <li key={i}>{it.text}{it.page ? <em> (p. {it.page})</em> : null}</li>
                ))}
                {g.items.length > MAX_ITEMS_PER_GROUP && (
                  <li className="desk-ledger-more">+{g.items.length - MAX_ITEMS_PER_GROUP} more</li>
                )}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DeskDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(undefined); // undefined = loading, null = signed out
  const [workspace, setWorkspace] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => onAuthStateChanged(auth, (u) => {
    setUser(u || null);
    if (!u) navigate('/desk/login');
  }), [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { workspace: ws } = await getWorkspace();
      setWorkspace(ws);
      const [{ contracts: list }, { tasks: openTasks }] = await Promise.all([
        listContracts(ws.id),
        listWorkspaceOpenTasks(ws.id),
      ]);
      setContracts(list);
      setTasks(openTasks);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  const completeTask = async (task) => {
    setTasks(prev => prev.filter(t => t.id !== task.id));
    try {
      await setTaskStatus(workspace.id, task.contractId, task.id, true);
    } catch (err) {
      setTasks(prev => [...prev, task]);
    }
  };

  const pickFile = async (file) => {
    if (!file) return;
    setUploadError('');
    if (!/\.pdf$/i.test(file.name)) { setUploadError('Please upload a PDF file.'); return; }
    if (file.size > MAX_FILE_BYTES) { setUploadError('That PDF is larger than 8MB — please upload a smaller file.'); return; }
    setUploading(true);
    try {
      const fileBase64 = await fileToBase64(file);
      const { contract } = await uploadContract(workspace.id, file.name, fileBase64);
      setContracts(prev => [contract, ...prev]);
    } catch (err) {
      setUploadError(err.message);
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const ledgers = buildLedgers(contracts);

  if (user === undefined || loading) {
    return <div className="desk-loading"><span /><span /><span /></div>;
  }

  return (
    <>
      <SEO title="Contract Desk — The PP Studio" description="Your contract operations dashboard." noIndex />
      <div className="desk-app">
        <header className="desk-topbar">
          <Link to="/" className="desk-logo"><img src={ppLogo} alt="The PP Studio" /><em>Contract Desk</em></Link>
          <div className="desk-topbar-right">
            {workspace && <span className="desk-plan-pill">{workspace.plan} plan</span>}
            <button type="button" onClick={() => signOut(auth)}>Sign out</button>
          </div>
        </header>

        <main className="desk-main">
          <div className="desk-main-head">
            <div>
              <h1>What needs your attention this week</h1>
              <p>{workspace ? `${contracts.length} active contract${contracts.length === 1 ? '' : 's'} in ${workspace.name}` : ' '}</p>
            </div>
            {workspace && (
              <div>
                <input ref={inputRef} type="file" accept="application/pdf,.pdf" hidden onChange={e => pickFile(e.target.files?.[0])} />
                <button type="button" className="desk-upload-btn" disabled={uploading} onClick={() => inputRef.current?.click()}>
                  {uploading ? 'Reading contract…' : '+ Add a contract'}
                </button>
              </div>
            )}
          </div>

          {uploadError && <p className="desk-error">{uploadError}</p>}

          {error && (
            <div className="desk-error">
              <p style={{ margin: '0 0 .6rem' }}>{error}</p>
              <button type="button" className="desk-upload-btn" onClick={load}>Try again</button>
            </div>
          )}

          {!error && workspace && contracts.length === 0 && (
            <div className="desk-empty">
              <p>No contracts yet. Add your first one to build your Contract Control Centre.</p>
            </div>
          )}

          {!error && tasks.length > 0 && (
            <div className="desk-tasks-section">
              <h2>Your tasks <span className="desk-task-count">{tasks.length} open</span></h2>
              <ul className="desk-dashboard-tasks">
                {tasks.map(t => {
                  const today = new Date().toISOString().slice(0, 10);
                  const overdue = t.dueDate && t.dueDate < today;
                  return (
                    <li key={t.id} className={overdue ? 'is-overdue' : ''}>
                      <input type="checkbox" onChange={() => completeTask(t)} aria-label={`Mark "${t.text}" done`} />
                      <span className="desk-dashboard-task-text">
                        {t.text}
                        <Link to={`/desk/contracts/${t.contractId}`}>{t.contractFilename}</Link>
                      </span>
                      {t.dueDate && <span className="desk-dashboard-task-due">{overdue ? 'Overdue · ' : ''}{fmtDueDate(t.dueDate)}</span>}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {!error && contracts.length > 0 && (
            <div className="desk-ledgers">
              <LedgerCard title="We owe" hint="What your business must deliver" items={ledgers.weOwe} emptyText="Nothing outstanding on your side." />
              <LedgerCard title="They owe" hint="What the other party must provide" items={ledgers.theyOwe} emptyText="Nothing outstanding from the other side." />
              <LedgerCard title="Money" hint="What should be invoiced, paid, or is due" items={ledgers.money} emptyText="No payment items found." />
              <LedgerCard title="Watch" hint="Attention flags and dates in the next 90 days" items={ledgers.watch} emptyText="Nothing needs a closer look right now." />
            </div>
          )}

          {!error && contracts.length > 0 && (
            <div className="desk-contract-list">
              <h2>All contracts</h2>
              <ul>
                {contracts.map(c => (
                  <li key={c.id}>
                    <Link to={`/desk/contracts/${c.id}`}>{c.filename}</Link>
                    <span>{c.pageCount ? `${c.pageCount} pages` : ''}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </main>
      </div>
    </>
  );
}